// Pages/admin/SubscriptionsPage.jsx (version modernisée avec typographie)
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiEye,
  FiToggleLeft,
  FiToggleRight,
  FiPackage,
  FiDollarSign,
  FiActivity,
  FiSearch,
  FiFilter,
  FiRefreshCw,
} from "react-icons/fi";
import { BsStars, BsLightningCharge } from "react-icons/bs";
import { BiCopy } from "react-icons/bi";

import { useSubscriptionPlanStore } from "../../store/subscriptionPlanStore";
import { toast } from "react-toastify";

export default function SubscriptionsPage() {
  const {
    subscriptions,
    loading,
    error,
    fetchSubscriptions,
    deleteSubscription,
    toggleSubscriptionStatus,
  } = useSubscriptionPlanStore();

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const handleDelete = async (id) => {
    try {
      await deleteSubscription(id);
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await toggleSubscriptionStatus(id, !currentStatus);
    } catch (error) {
      console.error("Erreur lors du changement de statut:", error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XAF",
    }).format(price);
  };

  // Filtrage des abonnements
  const filteredSubscriptions = subscriptions.filter((subscription) => {
    const term = searchTerm.toLowerCase();

    const matchesSearch =
      subscription.name.toLowerCase().includes(term) ||
      subscription.description.toLowerCase().includes(term) ||
      subscription.code.toLowerCase().includes(term) ||
      // Recherche dans chaque service inclus
      subscription.includedServices.some((item) =>
        item?.service?.nom.toLowerCase().includes(term),
      );

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && subscription.isActive) ||
      (statusFilter === "inactive" && !subscription.isActive);

    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: subscriptions.length,
    active: subscriptions.filter((sub) => sub.isActive).length,
    inactive: subscriptions.filter((sub) => !sub.isActive).length,
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié dans le presse-papier");
  };

  return (
    <div className="mx-auto">
      {/* En-tête avec statistiques */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h2 className="font-poppins font-semibold text-[18px] text-title">
              <FiPackage className="inline mr-4 text-secondary-600" size={36} />
              Abonnements
            </h2>
            <p className="font-nunito text-[14px] text-text">
              Gérez vos formules d'abonnement et boostez votre business
            </p>
          </div>
          <Link
            to="/admin/subscriptions/create"
            className="mt-6 lg:mt-0 w-fit justify-center gap-2 px-4 py-2 bg-secondary-600 text-white font-medium hover:bg-secondary-700 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center space-x-3 transition-all duration-200 hover:shadow-xl font-nunito text-[14px] font-semibold text-action"
          >
            <span>Nouvel abonnement</span>
            <FiPlus size={24} />
          </Link>
        </div>

        {/* Cartes de statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-poppins font-medium text-[15px] text-subtitle">
                  Total
                </p>
                <p className="font-poppins font-bold text-[16px] text-montant">
                  {stats.total}
                </p>
              </div>
              <div className="p-3 bg-secondary-50">
                <BsStars className="text-secondary-500" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-poppins font-medium text-[15px] text-subtitle">
                  Actifs
                </p>
                <p className="font-poppins font-bold text-[16px] text-montant text-green-600">
                  {stats.active}
                </p>
              </div>
              <div className="p-3 bg-green-50">
                <BsLightningCharge className="text-green-500" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-poppins font-medium text-[15px] text-subtitle">
                  Inactifs
                </p>
                <p className="font-poppins font-bold text-[16px] text-montant text-gray-400">
                  {stats.inactive}
                </p>
              </div>
              <div className="p-3 bg-gray-50">
                <FiToggleLeft className="text-gray-400" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Barre de recherche et filtres */}
        <div className="bg-white p-1 md:p-6 shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="text-gray-400" size={20} />
                </div>
                <input
                  type="text"
                  placeholder="Rechercher un abonnement..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-all duration-200 font-nunito text-[14px]"
                />
              </div>
            </div>

            <div className="md:flex items-center space-x-4 space-y-4 md:space-y-0">
              <div className="flex items-center space-x-2">
                <FiFilter className="text-gray-400" size={20} />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 px-4 py-2 focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-all duration-200 font-nunito text-[14px]"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="active">Actifs</option>
                  <option value="inactive">Inactifs</option>
                </select>
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("all");
                    fetchSubscriptions();
                  }}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-3 transition-all duration-200"
                >
                  <FiRefreshCw size={18} />
                </button>
              </div>

              <div className="flex justify-end"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 p-6">
          <div className="flex items-center space-x-3 text-red-800">
            <FiActivity size={24} />
            <div>
              <span className="font-poppins font-semibold text-[18px] text-title">
                {error}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Grille des abonnements */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredSubscriptions.map((subscription) => (
          <div
            key={subscription.id}
            className="bg-white border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 group"
          >
            {/* En-tête de la carte */}
            <div
              className={`p-2 md:p-6 border-b ${
                subscription.isActive
                  ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
                  : "bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <h3 className="font-poppins font-semibold text-[18px] text-title">
                    {subscription.name}
                  </h3>
                </div>
                <button
                  onClick={() =>
                    handleToggleStatus(subscription.id, subscription.isActive)
                  }
                  className={`p-2 transition-all duration-200 ${
                    subscription.isActive
                      ? "bg-green-100 text-green-600 hover:bg-green-200"
                      : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                  }`}
                >
                  {subscription.isActive ? (
                    <FiToggleRight size={20} />
                  ) : (
                    <FiToggleLeft size={20} />
                  )}
                </button>
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-2 text-sm mb-2">
                <label className="block font-poppins font-medium text-[15px] text-subtitle">
                  Code :
                </label>

                <div
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => copyToClipboard(subscription.code)}
                >
                  <span className="font-mono bg-gray-100 px-3 py-2 flex-1 font-nunito text-[14px]">
                    {subscription.code}
                  </span>
                  <button
                    onClick={() => copyToClipboard(subscription.code)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <BiCopy size={16} />
                  </button>
                </div>
              </div>

              <p className="font-nunito text-[14px] text-text mb-2 line-clamp-2">
                {subscription.description}
              </p>

              <div className="flex items-center justify-between">
                <span className="font-poppins font-bold text-[16px] text-montant">
                  {formatPrice(subscription.price)}
                </span>
                <span
                  className={`px-3 py-1 font-nunito text-[14px] font-semibold text-action ${
                    subscription.isActive
                      ? "bg-green-100 text-green-800 border border-green-200"
                      : "bg-gray-100 text-gray-800 border border-gray-200"
                  }`}
                >
                  {subscription.isActive ? "Actif" : "Inactif"}
                </span>
              </div>
            </div>

            {/* Corps de la carte */}
            <div className="p-2 md:p-6">
              <div className="mb-4">
                <h4 className="font-poppins font-semibold text-[18px] text-title mb-3 flex items-center">
                  <FiPackage className="mr-2 text-secondary-500" size={16} />
                  Services inclus
                </h4>
                {subscription.includedServices &&
                subscription.includedServices.length > 0 ? (
                  <ul className="space-y-2">
                    {subscription.includedServices.map(
                      (includedService, index) => (
                        <li
                          key={index}
                          className="flex items-center justify-between font-nunito text-[14px]"
                        >
                          <span className="text-text">
                            {includedService.service?.nom ||
                              `Service #${includedService.id}`}
                          </span>
                          <span className="text-gray-400 font-medium">
                            ×{includedService.quantity}
                          </span>
                        </li>
                      ),
                    )}
                  </ul>
                ) : (
                  <p className="font-nunito text-[14px] text-text italic">
                    Aucun service inclus
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2">
                <Link
                  to={`/admin/subscriptions/${subscription.id}`}
                  className="flex-1 bg-secondary-50 hover:bg-secondary-100 text-secondary-600 px-4 py-3 font-semibold text-center transition-all duration-200 hover:shadow-sm inline-flex items-center justify-center space-x-2 font-nunito text-[14px] font-semibold text-action"
                  title="Détails"
                >
                  <FiEye size={16} />
                  <span className="hidden lg:flex">Détails</span>
                </Link>
                <Link
                  to={`/admin/subscriptions/${subscription.id}/edit`}
                  className="flex-1 bg-green-50 hover:bg-green-100 text-green-600 px-4 py-3 font-semibold text-center transition-all duration-200 hover:shadow-sm inline-flex items-center justify-center space-x-2 font-nunito text-[14px] font-semibold text-action"
                  title="Modifier"
                >
                  <FiEdit3 size={16} />
                  <span className="hidden lg:flex">Modifier</span>
                </Link>
                {/* <button
                  onClick={() => setDeleteConfirm(subscription.id)}
                  className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 px-4 py-3 font-semibold transition-all duration-200 hover:shadow-sm inline-flex items-center justify-center space-x-2 font-nunito text-[14px] font-semibold text-action"
                  title="Supprimer"
                >
                  <FiTrash2 size={16} />
                  <span className="hidden xl:flex">Supprimer</span>
                </button> */}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Message si aucun abonnement */}
      {filteredSubscriptions.length === 0 && !loading && (
        <div className="text-center py-16">
          <FiPackage className="mx-auto text-gray-400 mb-4" size={64} />
          <h3 className="font-poppins font-semibold text-[18px] text-title mb-3">
            Aucun abonnement trouvé
          </h3>
          <p className="font-nunito text-[14px] text-text mb-8 text-lg">
            {searchTerm || statusFilter !== "all"
              ? "Aucun abonnement ne correspond à vos critères de recherche"
              : "Commencez par créer votre premier abonnement"}
          </p>
          <Link
            to="/admin/subscriptions/create"
            className="bg-gradient-to-r from-secondary-600 to-secondary-200 hover:from-secondary-700 hover:to-secondary-300 text-white p-3 font-semibold inline-flex items-center space-x-1 transition-all duration-200 hover:shadow-xl font-nunito text-[14px] font-semibold text-action"
          >
            <span>Créer un abonnement</span>
            <FiPlus size={24} />
          </Link>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white p-6 max-w-md w-full">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-red-50">
                <FiTrash2 className="text-red-500" size={24} />
              </div>
              <h3 className="font-poppins font-semibold text-[18px] text-title">
                Confirmer la suppression
              </h3>
            </div>
            <p className="font-nunito text-[14px] text-text mb-6">
              Êtes-vous sûr de vouloir supprimer cet abonnement ? Cette action
              est irréversible.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 font-semibold transition-all duration-200 font-nunito text-[14px] font-semibold text-action"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white px-6 py-3 font-semibold transition-all duration-200 font-nunito text-[14px] font-semibold text-action"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
